import Swiper, {
  Navigation,
  Thumbs,
  Keyboard,
  A11y,
  Zoom
} from 'swiper';

const breakpoints = {
  xxs: 0,
  xs: 320,
  xsm: 550,
  sm: 770,
  md: 1024,
  lg: 1230,
  xl: 1680,
  xxl: 1920,
  xxxl: 2500,
};

/**
 * Creates vehicle image carousel with event handling and syncing of multiple swipers.
 *
 * @class VehicleImageCarousel
 */
class VehicleImageCarousel {
  constructor (
    thumbsSwiper,
    mainSwiper,
    fullscreenSwiper,

  ) {
    this.thumbsSwiper = thumbsSwiper;
    this.mainSwiper = mainSwiper;
    this.fullscreenSwiper = fullscreenSwiper;

    this.fullScreenToggler = document.querySelector(
      '.vehicle-swiper-image-show__fullscreen'
    );

    this.overlayElement = document.querySelector(
      '.vehicle-swiper-image-carousel__overlay'
    );

    this.runningNumber = document.querySelector('.js-vehicle-image-carousel__running-number');

    this.fullScreenToggler.addEventListener('click', () =>
      this.openFullscreenSwiper()
    );

    this.thumbsSwiper.on('slideChange', swiper => {
      this.onThumbsSwiperSlideChange(swiper);
    });

    this.thumbsSwiper.on('click', (swiper) =>
      this.onThumbsSwiperSlideChange(swiper)
    );

    this.mainSwiper.on('slideChange', swiper =>
      this.onMainSwiperSlideChange(swiper)
    );

    window.addEventListener('resize', event => this.onWindowResize(event));


    this.overlayElement.addEventListener('click', event =>
      this.onOverlayElementClick()
    );
    document.body.addEventListener('keydown', event =>
      this.onBodyKeydown(event)
    );

  }


  /**
   * Slide past main swiper disabled slides on rewind.
   *
   * @param {Swiper} swiper - Swiper instance.
   * @memberof VehicleImageCarousel
   */
  onMainSwiperSlideChange(swiper) {
    this.runningNumber.innerHTML = swiper.realIndex + 1;
  }

  /**
   * Translate thumbs swiper to beginning on first enabled slide.
   *
   * @param {Swiper} swiper - Swiper instance.
   * @memberof VehicleImageCarousel
   */
  onThumbsSwiperSlideChange(swiper) {
    this.mainSwiper.loopFix();
  }

  /**
   * Closes fullscreen swiper, if open, on window resize below desktop breakpoint.
   *
   * @param {UIEvent} event - Window resize event.
   * @memberof VehicleImageCarousel
   */
  onWindowResize(event) {
    if (
      !this.fullscreenSwiper.el.hidden &&
      event.target.innerWidth < breakpoints.md
    ) {
      this.closeFullscreenSwiper();
    }
    this.mainSwiper.navigation.init();
    this.thumbsSwiper.navigation.init();
  }

  /**
   * Closes fullscreen swiper on overlay element click.
   *
   * @memberof VehicleImageCarousel
   */
  onOverlayElementClick() {
    this.closeFullscreenSwiper();
  }

  /**
   * Closes fullscreen swiper, if open, on body ESC key press.
   *
   * @param {KeyboardEvent} event - Body keydown event.
   * @memberof VehicleImageCarousel
   */
  onBodyKeydown(event) {
    if (
      !this.fullscreenSwiper.el.hidden &&
      (event.key === 'Escape' || event.key === 'Esc')
    ) {
      this.closeFullscreenSwiper();
    }
  }

  /**
   * Opens fullscreen swiper, and takes over keyboard control and active slide from main swiper.
   *
   * @memberof VehicleImageCarousel
   */
  openFullscreenSwiper() {
    this.mainSwiper.keyboard.disable();
    this.fullscreenSwiper.slideTo(this.mainSwiper.realIndex);
    this.fullscreenSwiper.keyboard.enable();
    this.fullscreenSwiper.el.hidden = false;
  }

  /**
   * Closes fullscreen swiper, and hands over keyboard control and active slide to main swiper.
   *
   * @memberof VehicleImageCarousel
   */
  closeFullscreenSwiper() {
    this.fullscreenSwiper.el.hidden = true;
    this.fullscreenSwiper.keyboard.disable();
    this.mainSwiper.slideToLoop(this.fullscreenSwiper.realIndex);
    this.mainSwiper.keyboard.enable();
    this.mainSwiper.loopFix();
  }
}

/**
 * Initializes vehicle image carousel.
 *
 * @return {VehicleImageCarousel} - Vehicle image carousel instance.
 */
const init = () => {
  const slides =  document.querySelectorAll('.swiper-main .swiper-slide').length > 1;

  /* Initialize thumbs swiper */
  const thumbsSwiper = new Swiper('.swiper-thumbs', {
    a11y: true,
    loop: slides,
    slidesPerGroup: 7,
    spaceBetween: 8,
    modules: [A11y, Keyboard, Navigation, Thumbs],
    preloadImages: true,
    slidesPerView: 'auto',
    centeredSlides: false,
    watchSlidesProgress: true,
    watchOverflow: true,

    /* navigation */
    navigation: {
      nextEl: '.swiper-thumbs-button-next',
      prevEl: '.swiper-thumbs-button-prev',
    },
    freeMode: true
  });


  /* Initialize main swiper */
  const mainSwiper = new Swiper('.swiper-main', {
    a11y: true,
    centeredSlides: true,
    slidesPerView: 'auto',
    initialSlide: 0,
    spaceBetween: 24,
    keyboard: true,
    modules: [A11y, Keyboard, Navigation, Thumbs],
    loop: slides,
    lazyPreloadPrevNext: 1,
    watchSlidesProgress: true,
    watchOverflow: true,

    /* navigation */
    navigation: {
      nextEl: (window.innerWidth < breakpoints.sm) ? '.swiper-mobile-button-next' : '.swiper-button-next',
      prevEl:  (window.innerWidth < breakpoints.sm) ? '.swiper-mobile-button-prev' : '.swiper-button-prev',
    },

    /* different breakpoints */
    breakpoints: {
      [breakpoints.xsm]: {
        spaceBetween: 8,
        navigation: {
          nextEl: '.swiper-mobile-button-next',
          prevEl: '.swiper-mobile-button-prev',
        },
      },
      [breakpoints.sm]: {
        spaceBetween: 8,
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
      },
      [breakpoints.md]: {
        spaceBetween: 8,
      },
      [breakpoints.lg]: {
        spaceBetween: 24,
      },

    },
    on: {
      init: function () {
        this.loopFix();
        this.navigation.init();
      }
    },
    preloadImages: false,
    thumbs: {
      swiper: thumbsSwiper,
    },
  });



  /* Initialize fullscreen swiper */
  const fullscreenSwiper = new Swiper('.swiper-fullscreen', {
    a11y: true,
    slidesPerView: 'auto',
    spaceBetween: '25%',
    loop: false,
    watchSlidesProgress: true,
    watchOverflow: true,
    modules: [A11y, Keyboard, Navigation,Zoom],
    navigation: {
      nextEl: '.swiper-full-button-next',
      prevEl: '.swiper-full-button-prev',
    },
    preloadImages: false,
    rewind: slides,
    zoom: {
      maxRatio: 3,
      minRatio: 1
    },
  });

  // Initialize vehicle image carousel
  const vehicleImageCarousel = new VehicleImageCarousel(
    thumbsSwiper,
    mainSwiper,
    fullscreenSwiper,
  );

  return vehicleImageCarousel;
};

export { init };
